import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import DownArrow from "../../assets/icons/down-arrow.svg";
import RedIcon from "../../assets/icons/Red-icon.svg";
import "./MultiContractDropDownStyle.css";

function MultiContractDropDown({ options, value, onChange, label, id, menuPlacement, placeholder = "Choose your contract account number" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const touchStartPos = useRef({ x: 0, y: 0 });
  const isScrolling = useRef(false);
  const scrollThreshold = 10;

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    isScrolling.current = false;
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

    if (deltaY > scrollThreshold || deltaX > scrollThreshold) {
      isScrolling.current = true;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(0);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      scrollIntoViewHandler(highlightedIndex);
    }
  }, [isOpen, highlightedIndex]);

  const scrollIntoViewHandler = (index, block = "nearest") => {
    const highlightedElement = listRef.current?.children[index];
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block });
    }
  };

  const handleSelect = (option, index) => {
    onChange(option);
    setIsOpen(false);
    const mainIndex = options.findIndex((opt) => opt.label === option.label);
    setSelectedIndex(mainIndex);
    setHighlightedIndex(0);
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " " || event.key === "touchstart") {
        let mainIndex = selectedIndex;
        if (mainIndex === -1 && value?.label) {
          mainIndex = options.findIndex((opt) => opt.label === value?.label);
          setSelectedIndex(mainIndex);
        }
        setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
        setTimeout(() => {
          mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
        }, 0);
      }
      return;
    }

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex], highlightedIndex);
        }
        break;
      case "Escape":
      case "Tab":
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex(0);
        setIsOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => {
          let nextIndex;
          if (prev < options.length - 1) {
            nextIndex = prev + 1;
          } else if (prev === options.length - 1) {
            nextIndex = 0;
          } else {
            nextIndex = prev;
          }
          return nextIndex;
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => {
          let nextIndex;
          if (prev > 0) {
            nextIndex = prev - 1;
          } else if (prev === 0) {
            nextIndex = options.length - 1;
          } else {
            nextIndex = prev;
          }
          return nextIndex;
        });
        break;
      default:
        break;
    }
  };

  const onClickHandler = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      let mainIndex = selectedIndex;
      if (mainIndex === -1 && value?.label) {
        mainIndex = options.findIndex((opt) => opt.label === value?.label);
        setSelectedIndex(mainIndex);
      }
      setHighlightedIndex((prev) => (mainIndex >= 0 ? -1 : prev));
      setTimeout(() => {
        mainIndex >= 0 ? scrollIntoViewHandler(mainIndex, "center") : scrollIntoViewHandler(0);
      }, 0);
    }
    buttonRef.current?.focus();
  };

  const onFocusHandler = (e) => {
    e.preventDefault();
    setIsFocused(true);
  };

  const onBlurHandler = () => {
    setIsFocused(false);
  };

  const onTouchEndHandler = (e) => {
    e.preventDefault();
    buttonRef.current.click();
  };

  const optionOnTouchEndHandler = (e, option, index) => {
    e.preventDefault();
    if (!isScrolling.current) {
      handleSelect(option, index);
    }
  };

  return (
    <>
      {label && (
        <label htmlFor={id} className="block text-lg font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className="dropdown-container">
        <button
          tabIndex={0}
          ref={buttonRef}
          id={id}
          type="button"
          className={`dropdown-button ${isFocused ? "dropdown-button-focused" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
          aria-activedescendant={highlightedIndex >= 0 && options[highlightedIndex] ? `${id}-option-${options[highlightedIndex]?.value}` : undefined}
          onClick={onClickHandler}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onTouchEnd={onTouchEndHandler}
          onKeyDown={handleKeyDown}
          disabled={false}
        >
          <span style={{ display: "flex", alignItems: "center" }} className={value?.label ? "dropdown-button-text" : "dropdown-button-placeholder"}>
            {value?.label ? (
              <>
                {value?.label}
                {value?.suspendStatus ? (
                  <img
                    aria-hidden="true"
                    src={RedIcon}
                    aria-label="Red Icon"
                    alt="red-icon"
                    style={{
                      width: "14px",
                      height: "20px",
                      marginLeft: "8px",
                      marginRight: "8px",
                    }}
                  />
                ) : null}
              </>
            ) : (
              placeholder
            )}
          </span>
          <img src={DownArrow} alt="" aria-hidden="true" className={`dropdown-arrow ${isOpen ? "dropdown-arrow-open" : ""}`} />
        </button>
        {isOpen && (
          <ul
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            className={`dropdown-list ${menuPlacement === "top" ? "open-upwards" : ""}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                id={`${id}-option-${option.value}`}
                role="option"
                tabIndex={option.isDisabled ? -1 : 0} // Remove from tab order if disabled
                aria-selected={value?.value === option.value}
                aria-disabled={option.isDisabled} // Accessibility for screen readers
                aria-label={option.label}
                className={`
                dropdown-option
                ${index === highlightedIndex ? "dropdown-option-highlighted" : ""}
                ${value?.value === option.value ? "dropdown-option-selected" : ""}
                ${option.isDisabled ? "dropdown-option-disabled" : ""}`}
                onClick={() => !option.isDisabled && handleSelect(option, index)}
                onMouseEnter={() => {
                  if (!option.isDisabled) {
                    setHighlightedIndex(index);
                  }
                }}
                onTouchEnd={(e) => !option.isDisabled && optionOnTouchEndHandler(e, option, index)}
                style={{
                  color: option.isDisabled ? "gray" : "black",
                  cursor: option.isDisabled ? "not-allowed" : "pointer",
                  pointerEvents: option.isDisabled ? "none" : "auto",
                  justifyContent: "flex-start",
                }}
              >
                {option.label}
                {option.isAccountSuspended && (
                  <img
                    aria-hidden="true"
                    src={RedIcon}
                    aria-label="Red Icon"
                    alt="red-icon"
                    style={{ width: "14px", height: "20px", marginLeft: "8px" }}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

MultiContractDropDown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      isAccountSuspended: PropTypes.bool,
      suspendStatus: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    isDisabled: PropTypes.bool,
    isAccountSuspended: PropTypes.bool,
    suspendStatus: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  menuPlacement: PropTypes.string,
  placeholder: PropTypes.string,
};

export default MultiContractDropDown;
